/**
 * Video Cover Block - Interactivity API View Script
 *
 * Provides reactive video controls using WordPress Interactivity API.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from "@wordpress/interactivity";

/**
 * Video Cover block store.
 *
 * Uses WordPress Interactivity API for reactive state management.
 */
store("brandy/video-cover", {
  /**
   * Actions for user interactions.
   */
  actions: {
    /**
     * Toggle video play/pause state.
     */
    togglePlay() {
      const ctx = getContext();
      const { ref } = getElement();
      const wrapper = ref.closest(".brandy-video-cover");

      if (ctx.videoType === "upload") {
        const video = wrapper.querySelector(".brandy-video-cover-video");
        if (video) {
          if (ctx.isPlaying) {
            video.pause();
          } else {
            video.play();
          }
          ctx.isPlaying = !ctx.isPlaying;
        }
      } else if (ctx.videoType === "youtube") {
        const iframe = wrapper.querySelector(".brandy-video-cover-iframe");
        if (iframe && iframe.contentWindow) {
          if (ctx.isPlaying) {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              "*"
            );
          } else {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"playVideo","args":""}',
              "*"
            );
          }
          ctx.isPlaying = !ctx.isPlaying;
        }
      } else if (ctx.videoType === "vimeo") {
        const iframe = wrapper.querySelector(".brandy-video-cover-iframe");
        if (iframe && iframe.contentWindow) {
          if (ctx.isPlaying) {
            iframe.contentWindow.postMessage('{"method":"pause"}', "*");
          } else {
            iframe.contentWindow.postMessage('{"method":"play"}', "*");
          }
          ctx.isPlaying = !ctx.isPlaying;
        }
      }
    },

    /**
     * Toggle video mute/unmute state.
     */
    toggleMute() {
      const ctx = getContext();
      const { ref } = getElement();
      const wrapper = ref.closest(".brandy-video-cover");

      if (ctx.videoType === "upload") {
        const video = wrapper.querySelector(".brandy-video-cover-video");
        if (video) {
          video.muted = !video.muted;
          ctx.isMuted = video.muted;
        }
      } else if (ctx.videoType === "youtube") {
        const iframe = wrapper.querySelector(".brandy-video-cover-iframe");
        if (iframe && iframe.contentWindow) {
          if (ctx.isMuted) {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"unMute","args":""}',
              "*"
            );
          } else {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"mute","args":""}',
              "*"
            );
          }
          ctx.isMuted = !ctx.isMuted;
        }
      } else if (ctx.videoType === "vimeo") {
        const iframe = wrapper.querySelector(".brandy-video-cover-iframe");
        if (iframe && iframe.contentWindow) {
          if (ctx.isMuted) {
            iframe.contentWindow.postMessage(
              '{"method":"setVolume","value":1}',
              "*"
            );
          } else {
            iframe.contentWindow.postMessage(
              '{"method":"setVolume","value":0}',
              "*"
            );
          }
          ctx.isMuted = !ctx.isMuted;
        }
      }
    },
  },

  /**
   * Callbacks run on specific lifecycle events.
   */
  callbacks: {
    /**
     * Initialize the video player.
     * Called via data-wp-init directive when block is rendered.
     */
    initVideo() {
      const ctx = getContext();
      const { ref } = getElement();

      if (ctx.videoType === "upload") {
        const video = ref.querySelector(".brandy-video-cover-video");
        if (video) {
          // Listen for play/pause events to sync state.
          video.addEventListener("play", () => {
            ctx.isPlaying = true;
          });

          video.addEventListener("pause", () => {
            ctx.isPlaying = false;
          });

          // Handle autoplay.
          if (ctx.autoplay && ctx.isMuted) {
            video.play().catch(() => {
              // Autoplay failed (browser restriction).
              ctx.isPlaying = false;
            });
          }
        }
      }

      // Return cleanup function.
      return () => {
        // Cleanup if needed.
      };
    },
  },
});
